import { useCallback, useState, useContext } from "react";
import { GoogleMap, useLoadScript } from "@react-google-maps/api";

import { branchSwitchContext } from "../../contexts/BranchSwitchProvider";
import LoaderShimmer from "../LoaderShimmer";
import useDrivers from "./useDrivers";
import DriverMarkers from "./DriverMarkers";
import DriverInfoWindow from "./DriverInfoWindow";
import useBranch from "./useBranch";
import BranchMarker from "./BranchMarker";
import useCustomers from "./useCustomers";
import CustomerMarkers from "./CustomerMarkers";
import RouteLines from "./RouteLines";

const MAP_LIBRARIES = ["marker"];

const Map = () => {
  const [center] = useState({ lat: 29.2266, lng: 47.9689 });
  const [selectedDriver, setSelectedDriver] = useState(null);

  const [map, setMap] = useState(null);

  const { branchSwitch } = useContext(branchSwitchContext);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries: MAP_LIBRARIES,
  });

  const { drivers, updateSelectedDriver, followTargetId } = useDrivers(
    branchSwitch,
    selectedDriver,
    setSelectedDriver
  );

  const { branch } = useBranch(branchSwitch);
  const { customers } = useCustomers(branchSwitch);

  const containerStyle = {
    width: "100%",
    height: window.innerWidth < 768 ? "50vh" : "25vh",
  };

  const handleSelect = useCallback(
    (driverId) => updateSelectedDriver(driverId, false),
    // eslint-disable-next-line
    []
  );

  const renderOrderStatus = (status) => {
    switch (status) {
      case "0":
        return <div className="badge bg-warning">Pending</div>;
      case "1":
        return <div className="badge bg-warning">Waiting for Pickup</div>;
      case "2":
        return <div className="badge bg-info">Driver Arrived</div>;
      case "3":
      case "4":
        return <div className="badge bg-secondary">Order Scan</div>;
      case "5":
        return <div className="badge bg-success">Order Enroute</div>;
      default:
        return <div className="badge bg-dark">Unknown</div>;
    }
  };

  if (!isLoaded) {
    return (
      <div style={containerStyle}>
        <LoaderShimmer />
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={12}
      onLoad={(instance) => setMap(instance)}
      onUnmount={() => setMap(null)}
      options={{
        mapId: process.env.REACT_APP_GOOGLE_MAP_ID,
        optimized: true,
        streetViewControl: false,
        disableDefaultUI: true,
        fullscreenControl: true,
        gestureHandling: "greedy",
      }}
    >
      <DriverMarkers
        drivers={drivers}
        onSelect={handleSelect}
        followTargetId={followTargetId}
        map={map}
      />

      <BranchMarker branch={branch} map={map} />

      <CustomerMarkers customers={customers} map={map} />

      <RouteLines drivers={drivers} map={map} />

      <DriverInfoWindow
        driver={selectedDriver}
        renderOrderStatus={renderOrderStatus}
        onClose={() => setSelectedDriver(null)}
        updateSelectedDriver={updateSelectedDriver}
        followTargetId={followTargetId}
      />
    </GoogleMap>
  );
};

export default Map;